"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";

type NavProps = {
  ctaHref?: string;
  ctaLabel?: string;
};

const LINKS = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/services", label: "Services" },
  { href: "/blog", label: "Blog" },
  { href: "/appointment", label: "Appointment" },
  { href: "/contact", label: "Contact" },
];

export default function Nav({
  ctaHref = "/appointment",
  ctaLabel = "Book an Appointment",
}: NavProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  return (
    <>
      <nav className="nav">
        {/* Logo */}
        <Link href="/" className="nav-logo" onClick={closeMenu}>
          <Image
            src="/assets/logo-black.png"
            alt="Quickint Solutions"
            className="logo-img logo-black"
            width={224}
            height={18}
          />
          <Image
            src="/assets/logo-white.png"
            alt=""
            className="logo-img logo-white"
            width={224}
            height={18}
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="nav-links">
          {LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={pathname === link.href ? "active" : ""}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right Side */}
        <div className="nav-right">
          <Link
            href={ctaHref}
            className="nav-cta desktop-cta"
            onClick={closeMenu}
          >
            <span className="dot" />
            {ctaLabel}
          </Link>

          {/* Mobile Hamburger */}
          <button
            className={`nav-toggle ${isOpen ? "open" : ""}`}
            onClick={toggleMenu}
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      {/* Overlay */}
      <div
        id="mobile-navigation"
        className={`nav-mobile-menu ${isOpen ? "open" : ""}`}
        onClick={closeMenu}
        aria-hidden={!isOpen}
        hidden={!isOpen}
      >
        <nav
          aria-label="Mobile navigation"
          className="nav-mobile-links"
          onClick={(e) => e.stopPropagation()}
        >
          {LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={pathname === link.href ? "active" : ""}
              onClick={closeMenu}
            >
              {link.label}
            </Link>
          ))}

          <Link
            href={ctaHref}
            className="nav-cta mobile-cta"
            onClick={closeMenu}
          >
            <span className="dot" />
            {ctaLabel}
          </Link>
        </nav>
      </div>
    </>
  );
}
